import { fnstate } from '@srfnstack/fntags'

class EventEmitter {
  constructor () {
    this.listeners = {}
  }

  on (event, callback) {
    if (!this.listeners[event]) this.listeners[event] = []
    this.listeners[event].push(callback)
  }

  off (event, callback) {
    if (!this.listeners[event]) return
    this.listeners[event] = this.listeners[event].filter(cb => cb !== callback)
  }

  emit (event, data) {
    if (!this.listeners[event]) return
    this.listeners[event].forEach(cb => cb(data))
  }
}

export const eventBus = new EventEmitter()
export const user = fnstate({ loggedIn: false, showSignup: false })
export const loadingSession = fnstate(true)
const storedExtensionId = localStorage.getItem('etherbuildr_extension_id')
export const extensionId = fnstate(storedExtensionId)

export const checkSession = async () => {
  try {
    const res = await fetch('/api/auth/me')
    if (res.ok) {
      const data = await res.json()
      user({ loggedIn: true, ...data })
    }
  } catch (err) {
    console.error('Failed to check session:', err)
  } finally {
    loadingSession(false)
  }
}

export const credits = fnstate(0)
export const creditActions = fnstate([])
export const sidebarOpen = fnstate(false)
export const notification = fnstate({
  show: false,
  message: '',
  type: 'info', // 'info', 'success', 'warning', 'error'
  link: null, // { text: 'string', href: 'string' }
  position: 'toast-top toast-end'
})

export const showNotification = (message, type = 'info', link = null, position = 'toast-top toast-end') => {
  notification({ show: true, message, type, link, position })
  setTimeout(() => {
    notification.assign({ show: false })
  }, 5000)
}

export const refreshCredits = async () => {
  try {
    const [cRes, aRes] = await Promise.all([
      fetch('/api/system/credits'),
      fetch('/api/system/credit-actions')
    ])
    if (cRes.ok) {
      const data = await cRes.json()
      credits(data.amount)
    }
    if (aRes.ok) {
      creditActions(await aRes.json())
    }
  } catch (err) {
    console.error('Failed to refresh credits:', err)
  }
}
export const lastJobUpdate = fnstate(null)

export const modalState = fnstate({
  isOpen: false,
  title: '',
  message: '',
  type: 'alert', // 'alert', 'confirm', 'prompt', 'select', 'mockup-options'
  value: '', // for prompt / mockup-options instructions
  modelValue: '', // for mockup-options model
  options: [], // for select / mockup-options providers
  showFramedArtwork: false, // for mockup-options: offer the painted-frame toggle
  onConfirm: null,
  onCancel: null
})

window.alert = (message, title = 'Alert') => {
  return new Promise((resolve) => {
    modalState({
      isOpen: true,
      title,
      message,
      type: 'alert',
      value: '',
      onConfirm: () => {
        modalState.assign({ isOpen: false })
        resolve()
      },
      onCancel: () => {
        modalState.assign({ isOpen: false })
        resolve()
      }
    })
  })
}

window.confirm = (message, title = 'Confirm') => {
  return new Promise((resolve) => {
    modalState({
      isOpen: true,
      title,
      message,
      type: 'confirm',
      value: '',
      onConfirm: () => {
        modalState.assign({ isOpen: false })
        resolve(true)
      },
      onCancel: () => {
        modalState.assign({ isOpen: false })
        resolve(false)
      }
    })
  })
}

window.prompt = (message, defaultValue = '', title = 'Input Required') => {
  return new Promise((resolve) => {
    modalState({
      isOpen: true,
      title,
      message,
      type: 'prompt',
      value: defaultValue,
      onConfirm: (val) => {
        modalState.assign({ isOpen: false })
        resolve(val)
      },
      onCancel: () => {
        modalState.assign({ isOpen: false })
        resolve(null)
      }
    })
  })
}

// Shows a dialog with a model selector and optional instructions text field.
// Pass showFramedArtwork to add the "frame is part of the artwork" toggle —
// only offered where the caller actually forwards it to the job payload.
// Resolves to { model, instructions, framedArtwork } or null if cancelled.
window.promptMockupOptions = (defaultModel, providers, title = 'Generation Options', showFramedArtwork = false) => {
  return new Promise((resolve) => {
    modalState({
      isOpen: true,
      title,
      message: '',
      type: 'mockup-options',
      value: '',
      modelValue: defaultModel,
      options: providers,
      showFramedArtwork,
      onConfirm: (result) => {
        modalState.assign({ isOpen: false })
        resolve(result)
      },
      onCancel: () => {
        modalState.assign({ isOpen: false })
        resolve(null)
      }
    })
  })
}

window.promptSelect = (message, options = [], defaultValue = '', title = 'Selection Required') => {
  return new Promise((resolve) => {
    modalState({
      isOpen: true,
      title,
      message,
      type: 'select',
      value: defaultValue || (options[0]?.value || options[0]),
      options,
      onConfirm: (val) => {
        modalState.assign({ isOpen: false })
        resolve(val)
      },
      onCancel: () => {
        modalState.assign({ isOpen: false })
        resolve(null)
      }
    })
  })
}
